import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges
} from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { Config } from "./input-otp-multiple.config";
import { KeysPipe } from "./input-otp-multiple.pipe";
import { KeyboardUtil } from "./input-otp-multiple.util";

@Component({
  selector: "kwikui-input-otp-multiple",
  templateUrl: "./input-otp-multiple.component.html",
  styleUrls: ["./input-otp-multiple.component.scss"]
})
export class KwikUIInputOtpMultipleComponent implements OnInit, AfterViewInit {
  @Input() config: Config = { length: 4, size: "m" };

  @Input() disabled: boolean | null = false;

  @Input() formCtrl: FormControl;

  @Input() invalid: boolean | null = false;

  @Input() readOnly: boolean | null = false;

  @Output() onInputChange = new EventEmitter<string>();

  otpForm: FormGroup;

  currentVal: string;

  inputControls: FormControl[] = new Array(this.config.length);

  componentKey =
    Math.random().toString(36).substring(2) + new Date().getTime().toString(36);

  get inputType() {
    return this.config?.isPasswordInput
      ? "password"
      : this.config?.allowNumbersOnly
        ? "tel"
        : "text";
  }

  constructor(private keysPipe: KeysPipe) {}

  ngOnInit() {
    this.otpForm = new FormGroup({});
    for (let index = 0; index < this.config.length; index++) {
      this.otpForm.addControl(this.getControlName(index), new FormControl());
    }

    this.invalid = this.invalid === undefined ? false : this.invalid;
    this.readOnly = this.readOnly === undefined ? false : this.readOnly;
    this.disabled = this.disabled === undefined ? false : this.disabled;

    this.disableInputs();

    this.updateValue();

    this.otpForm.valueChanges.subscribe((v: object) => {
      this.keysPipe.transform(this.otpForm.controls).forEach((k) => {
        const val = this.otpForm.controls[k].value;
        if (val && val.length > 1) {
          if (val.length >= this.config.length) {
            this.setValue(val);
          } else {
            this.rebuildValue();
          }
        }
      });
    });
  }

  updateValue() {
    for (let index = 0; index < this.config.length; index++) {
      this.otpForm.controls[this.getControlName(index)].patchValue(
        this.formCtrl.value.charAt(index)
      );
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty("formCtrl") && !changes.formCtrl.firstChange) {
      this.formCtrl = changes.formCtrl.currentValue;
      this.updateValue();
    }
    if (changes.hasOwnProperty("invalid") && !changes.invalid.firstChange) {
      this.invalid = Boolean(changes.invalid.currentValue);
    }
    if (changes.hasOwnProperty("readOnly") && !changes.readOnly.firstChange) {
      this.readOnly = Boolean(changes.readOnly.currentValue);
    }
    if (changes.hasOwnProperty("disabled") && !changes.disabled.firstChange) {
      this.disabled = Boolean(changes.disabled.currentValue);
      this.disableInputs();
    }
    if (changes.hasOwnProperty("config") && !changes.config.firstChange) {
      this.config = changes.config.currentValue;
    }
  }

  ngAfterViewInit(): void {
    if (!this.config.disableAutoFocus) {
      const containerItem = document.getElementById(`c_${this.componentKey}`);
      if (containerItem) {
        const ele: any = containerItem.getElementsByClassName("otp-input")[0];
        if (ele && ele.focus) {
          ele.focus();
        }
      }
    }
  }

  private getControlName(idx) {
    return `ctrl_${idx}`;
  }

  disableInputs() {
    if (this.disabled) {
      this.formCtrl.disable({ emitEvent: false });
      this.otpForm.disable({ emitEvent: false });
      this.keysPipe.transform(this.otpForm.controls).forEach((k) => {
        this.otpForm.controls[k].disable({ emitEvent: false });
      });
    } else {
      this.formCtrl.enable({ emitEvent: false });
      this.otpForm.enable({ emitEvent: false });
      this.keysPipe.transform(this.otpForm.controls).forEach((k) => {
        this.otpForm.controls[k].enable({ emitEvent: false });
      });
    }
  }

  onKeyDown($event, inputIdx) {
    if (KeyboardUtil.ifSpacebar($event)) {
      $event.preventDefault();
      return false;
    }
  }

  onInput($event) {
    const newVal = this.currentVal
      ? `${this.currentVal}${$event.target.value}`
      : $event.target.value;
    if (this.config.allowNumbersOnly && !this.validateNumber(newVal)) {
      $event.target.value = "";
      $event.stopPropagation();
      $event.preventDefault();
    }
  }

  onKeyUp($event, inputIdx) {
    const nextInputId = this.appendKey(`otp_${inputIdx + 1}`);
    const prevInputId = this.appendKey(`otp_${inputIdx - 1}`);
    if (KeyboardUtil.ifRightArrow($event)) {
      $event.preventDefault();
      this.setSelected(nextInputId);
      return;
    }
    if (KeyboardUtil.ifLeftArrow($event)) {
      $event.preventDefault();
      this.setSelected(prevInputId);
      return;
    }
    if (KeyboardUtil.ifBackspaceOrDelete($event) && !$event.target.value) {
      this.setSelected(prevInputId);
      this.rebuildValue();
      return;
    }

    if (!$event.target.value) {
      return;
    }

    if (this.ifValidKeyCode($event)) {
      this.setSelected(nextInputId);
    }
    this.rebuildValue();
  }

  validateNumber(val) {
    return val && /^\d*\.?\d*$/.test(val);
  }

  appendKey(id) {
    return `${id}_${this.componentKey}`;
  }

  setSelected(eleId) {
    this.focusTo(eleId);
    const ele: any = document.getElementById(eleId);
    if (ele && ele.setSelectionRange) {
      setTimeout(() => {
        ele.setSelectionRange(0, 1);
      }, 0);
    }
  }

  ifValidKeyCode(event) {
    const inp = event.key;
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    return (
      isMobile ||
      /[a-zA-Z0-9-_]/.test(inp) ||
      (this.config.allowKeyCodes &&
        this.config.allowKeyCodes.includes(event.keyCode))
    );
  }

  focusTo(eleId) {
    const ele: any = document.getElementById(eleId);
    if (ele) {
      ele.focus();
    }
  }

  // method to set component value

  setValue(value: any) {
    if (this.config.allowNumbersOnly && isNaN(value)) {
      return;
    }
    this.otpForm.reset();
    if (!value) {
      this.rebuildValue();
      return;
    }
    value = value.toString().replace(/\s/g, ""); // remove whitespace
    Array.from(value).forEach((c, idx) => {
      if (this.otpForm.get(this.getControlName(idx))) {
        this.otpForm.get(this.getControlName(idx)).setValue(c);
      }
    });
    if (!this.config.disableAutoFocus) {
      const containerItem = document.getElementById(`c_${this.componentKey}`);
      const indexOfElementToFocus =
        value.length < this.config.length
          ? value.length
          : this.config.length - 1;
      const ele: any =
        containerItem.getElementsByClassName("otp-input")[
          indexOfElementToFocus
        ];
      if (ele && ele.focus) {
        ele.focus();
      }
    }
    this.rebuildValue();
  }

  rebuildValue() {
    let val = "";
    this.keysPipe.transform(this.otpForm.controls).forEach((k) => {
      if (this.otpForm.controls[k].value) {
        let ctrlVal = this.otpForm.controls[k].value;
        const isLengthExceed = ctrlVal.length > 1;
        let isCaseTransformEnabled =
          !this.config.allowNumbersOnly &&
          this.config.letterCase &&
          (this.config.letterCase.toLocaleLowerCase() == "upper" ||
            this.config.letterCase.toLocaleLowerCase() == "lower");
        ctrlVal = ctrlVal[0];
        const transformedVal = isCaseTransformEnabled
          ? this.config.letterCase.toLocaleLowerCase() == "upper"
            ? ctrlVal.toUpperCase()
            : ctrlVal.toLowerCase()
          : ctrlVal;
        if (isCaseTransformEnabled && transformedVal == ctrlVal) {
          isCaseTransformEnabled = false;
        } else {
          ctrlVal = transformedVal;
        }
        val += ctrlVal;
        if (isLengthExceed || isCaseTransformEnabled) {
          this.otpForm.controls[k].setValue(ctrlVal);
        }
      }
    });
    if (this.formCtrl?.setValue) {
      this.formCtrl.setValue(val);
    }
    this.onInputChange.emit(val);
    this.currentVal = val;
  }

  handlePaste(e) {
    // Get pasted data via clipboard API
    const clipboardData = e.clipboardData || (window as any).clipboardData;
    if (clipboardData) {
      var pastedData = clipboardData.getData("Text");
    }
    // Stop data actually being pasted into div
    e.stopPropagation();
    e.preventDefault();
    if (
      !pastedData ||
      (this.config.allowNumbersOnly && !this.validateNumber(pastedData))
    ) {
      return;
    }
    this.setValue(pastedData);
  }
}
